/**
 * LocationsGrandTotal — five-card summary row aggregating every running
 * location's metrics into a single "across all locations" view.
 *
 * Always rendered above the per-location cards/table on the GHL middleware
 * landing page. Refreshes whenever the page-level TimeRangeFilter changes,
 * since the same `useGHLLocationOverview` hook feeds all three views.
 *
 * Mirrors the StatCard pattern used by AnalyticsOverview / FollowUpStatsCards
 * / QualificationTab — kept consistent with the existing dashboard idiom.
 */

import { useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type GHLLocationOverview,
  formatCost,
  formatTokenCount,
  calculateTotalCost,
} from '@/lib/ghl-dashboard'
import { BarChart3, CheckCircle, Cpu, Clock, MapPin } from 'lucide-react'

interface Props {
  readonly data: readonly GHLLocationOverview[]
  readonly isLoading: boolean
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
}: {
  title: string
  value: string
  subtitle?: string
  icon: React.ComponentType<{ className?: string }>
  isLoading: boolean
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-7 w-24 mt-1" />
            ) : (
              <p className="text-2xl font-bold truncate">{value}</p>
            )}
            {subtitle && !isLoading && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
            )}
          </div>
          <Icon className="h-8 w-8 text-muted-foreground/50 shrink-0 ml-2" />
        </div>
      </CardContent>
    </Card>
  )
}

export function LocationsGrandTotal({ data, isLoading }: Props) {
  // Aggregate across all locations. Wrapped in useMemo so it doesn't
  // recompute on every parent render — only when the data array changes.
  // calculateTotalCost is called per-location, then summed: doing it in one
  // call with summed tokens would lose accuracy when locations use different
  // Claude models (different $/token rates).
  const totals = useMemo(() => {
    const totalAnalyses = data.reduce((acc, loc) => acc + loc.analysis_count, 0)
    const totalBookings = data.reduce((acc, loc) => acc + loc.bookings_detected, 0)
    const totalInputTokens = data.reduce((acc, loc) => acc + loc.total_input_tokens, 0)
    const totalOutputTokens = data.reduce((acc, loc) => acc + loc.total_output_tokens, 0)
    const totalCost = data.reduce(
      (acc, loc) => acc + calculateTotalCost(loc.total_input_tokens, loc.total_output_tokens),
      0
    )
    const totalPendingFollowups = data.reduce((acc, loc) => acc + loc.pending_followups, 0)
    const bookingRate =
      totalAnalyses > 0 ? (totalBookings / totalAnalyses) * 100 : 0

    return {
      totalAnalyses,
      totalBookings,
      totalInputTokens,
      totalOutputTokens,
      totalCost,
      totalPendingFollowups,
      bookingRate,
      activeLocations: data.length,
    }
  }, [data])

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      <StatCard
        title="Total Analyses"
        value={totals.totalAnalyses.toLocaleString()}
        subtitle={`${totals.activeLocations} location${totals.activeLocations === 1 ? '' : 's'}`}
        icon={BarChart3}
        isLoading={isLoading}
      />
      <StatCard
        title="Total Bookings"
        value={totals.totalBookings.toLocaleString()}
        subtitle={
          totals.totalAnalyses > 0
            ? `${totals.bookingRate.toFixed(1)}% booking rate`
            : 'No analyses yet'
        }
        icon={CheckCircle}
        isLoading={isLoading}
      />
      <StatCard
        title="AI Cost"
        value={formatCost(totals.totalCost)}
        subtitle={`${formatTokenCount(totals.totalInputTokens + totals.totalOutputTokens)} tokens`}
        icon={Cpu}
        isLoading={isLoading}
      />
      <StatCard
        title="Pending Follow-ups"
        value={totals.totalPendingFollowups.toLocaleString()}
        subtitle={
          totals.totalPendingFollowups > 0 ? 'SMS in flight' : 'Drip queue clear'
        }
        icon={Clock}
        isLoading={isLoading}
      />
      <StatCard
        title="Active Locations"
        value={totals.activeLocations.toLocaleString()}
        subtitle={
          totals.activeLocations === 0 ? 'No data in range' : 'Receiving webhooks'
        }
        icon={MapPin}
        isLoading={isLoading}
      />
    </div>
  )
}
